import DeactivateOrganizationWorkflow from '@Application/Organizations/Workflows/Commands/DeactivateOrganizationWorkflow';
import deactivateOrganizationWorkflow from '@Application/Organizations/Workflows/deactivateOrganizationWorkflow';
import { PermissionEnum } from '@Application/Security/Roles';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import useHasPermission from '@UI/Providers/PermissionProvider/useHasPermission';
import * as React from 'react';
import { useDispatch } from '@verclam/synapse';
import { Button, ConfirmDialog } from '@verclam/ui-kit';

type Props = {
	readonly organizationId: number;
	readonly workflowKey: string;
	readonly workflowLabel: string;
};

/**
 * Leaf action: the button and its confirmation. Archiving is reversible and loses nothing — the
 * preparations already open keep their frozen version — so a plain `ConfirmDialog` is enough.
 */
const ArchiveDocumentGroupAction: React.FC<Props> = ({ organizationId, workflowKey, workflowLabel }) => {
	const dispatch = useDispatch();
	const canWrite = useHasPermission(PermissionEnum.ORGANIZATION_WRITE);
	const [isConfirmVisible, setIsConfirmVisible] = React.useState(false);

	const handleArchiveClick = React.useCallback(() => {
		setIsConfirmVisible(true);
	}, []);

	const handleHide = React.useCallback(() => {
		setIsConfirmVisible(false);
	}, []);

	const handleConfirm = React.useCallback(() => {
		dispatch(
			deactivateOrganizationWorkflow(new DeactivateOrganizationWorkflow(organizationId, workflowKey)),
		).catch(ignoreHandledError);
		setIsConfirmVisible(false);
	}, [dispatch, organizationId, workflowKey]);

	if (!canWrite) {
		return null;
	}

	return (
		<React.Fragment>
			<Button
				type="button"
				severity="secondary"
				label="Archiver le groupe"
				onClick={handleArchiveClick}
			/>
			<ConfirmDialog
				blockScroll
				closable
				draggable={false}
				visible={isConfirmVisible}
				onHide={handleHide}
				header={`Archiver « ${workflowLabel} » ?`}
				message={
					<p>
						Il ne sera plus proposé aux professionnels. Les préparations déjà ouvertes avec ce
						groupe peuvent être menées à terme.
					</p>
				}
				acceptSeverity="danger"
				acceptLabel="Archiver le groupe"
				rejectLabel="Annuler"
				accept={handleConfirm}
				reject={handleHide}
			/>
		</React.Fragment>
	);
};

export default ArchiveDocumentGroupAction;
